import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Download, ScrollText } from 'lucide-react';
import { formatDate, type LogLease } from '@vuokramani/shared';
import { pageStyles as s } from '../../components/page.tsx';
import { Panel } from '../../components/ui.tsx';
import { leaseLogPdfUrl, propertyLogPdfUrl, useLogs } from '../../lib/logs.ts';
import l from './logs.module.css';

/**
 * Tili-sivun "Vuokrasuhteiden lokit": vuokranantajalle asunnoittain (koko asunto ja
 * jokainen vuokrasuhde), vuokralaiselle omat vuokrasuhteet. Näytä tai lataa PDF.
 */
export function LogsSection() {
  const { t } = useTranslation();
  const { data } = useLogs();
  const landlord = useLocation().pathname.startsWith('/vuokranantaja');
  if (!data) return null;
  const owned = landlord ? data.owned : [];
  const rented = landlord ? [] : data.rented;
  if (owned.length === 0 && rented.length === 0) return null;

  return (
    <section>
      <h2 className={s.sectionTitle}>{t('log.sectionTitle')}</h2>
      <p className={l.note}>{landlord ? t('log.sectionNote') : t('log.sectionNoteTenant')}</p>
      <Panel>
        {owned.map((p) => (
          <div key={p.propertyId} className={l.group}>
            <LogRow
              title={t('log.propertyTitle', { property: p.title })}
              meta={p.archived ? t('financePage.archived') : t('log.allLeases')}
              view={`/vuokranantaja/loki/asunto/${p.propertyId}`}
              pdf={propertyLogPdfUrl(p.propertyId)}
              strong
            />
            {p.leases.map((lease) => (
              <LeaseRow key={lease.leaseId} lease={lease} base="/vuokranantaja/loki/vuokrasuhde" />
            ))}
          </div>
        ))}
        {rented.map((lease) => (
          <LeaseRow key={lease.leaseId} lease={lease} base="/vuokralainen/loki" showProperty />
        ))}
      </Panel>
    </section>
  );
}

function LeaseRow({
  lease,
  base,
  showProperty = false,
}: {
  lease: LogLease;
  base: string;
  showProperty?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const period = `${formatDate(lease.startDate, i18n.language)} – ${
    lease.endDate ? formatDate(lease.endDate, i18n.language) : t('invoices.untilFurther')
  }`;
  const meta = [
    lease.tenants.join(', '),
    lease.status === 'ended' ? t('log.ended') : null,
    lease.accessUntil
      ? t('log.accessUntil', { date: formatDate(lease.accessUntil, i18n.language) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <LogRow
      title={showProperty ? `${lease.propertyTitle}, ${period}` : period}
      meta={meta}
      view={`${base}/${lease.leaseId}`}
      pdf={leaseLogPdfUrl(lease.leaseId)}
    />
  );
}

function LogRow({
  title,
  meta,
  view,
  pdf,
  strong = false,
}: {
  title: string;
  meta: string;
  view: string;
  pdf: string;
  strong?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={l.row}>
      <ScrollText size={18} aria-hidden="true" className={l.icon} />
      <div className={l.text}>
        <span className={strong ? l.titleStrong : l.title}>{title}</span>
        {meta && <span className={l.meta}>{meta}</span>}
      </div>
      <div className={l.actions}>
        <Link to={view}>{t('log.view')}</Link>
        <a href={pdf} download>
          <Download size={14} aria-hidden="true" />
          PDF
        </a>
      </div>
    </div>
  );
}
